import { useCallback, useState } from "react";
import { CountriesApi } from "../../infrastructure/api/CountriesApi";
import { StatesApi } from "../../infrastructure/api/StatesApi";
import { CitiesApi } from "../../infrastructure/api/CitiesApi";
import { DesignationsApi } from "../../infrastructure/api/DesignationsApi";
import { AgreementApi } from "../../infrastructure/api/AgreementApi";
import { getCountries } from "../useCases/getCountries";
import { getStates } from "../useCases/getStates";
import { getCities } from "../useCases/getCities";
import { getDesignations } from "../useCases/getDesignations";
import {
  getAgreements,
  getRequiredRegistrationClauses,
  type AgreementData,
} from "../useCases/getAgreements";
import type { Country } from "../../domain/entities/Countries";
import type { States } from "../../domain/entities/States";
import type { Cities } from "../../domain/entities/Cities";
import type { Designations } from "../../domain/entities/Designations";
import type {
  RegistrationAgreementClause,
  RegistrationScopeFilters,
} from "../../domain/entities/Agreement";

export const useOnboardingData = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCountries = useCallback(async (): Promise<Country[]> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new CountriesApi();
      return await getCountries(repo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStates = useCallback(async (countryId: string): Promise<States[]> => {
    try {
      const repo = new StatesApi();
      return await getStates(repo, countryId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, []);

  const fetchCities = useCallback(async (stateId: string): Promise<Cities[]> => {
    try {
      const repo = new CitiesApi();
      return await getCities(repo, stateId);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    }
  }, []);

  const fetchDesignations = useCallback(async (): Promise<Designations[]> => {
    setLoading(true);
    setError(null);
    try {
      const repo = new DesignationsApi();
      return await getDesignations(repo);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAgreements = useCallback(
    async (filters?: RegistrationScopeFilters): Promise<AgreementData> => {
      setLoading(true);
      setError(null);
      try {
        const repo = new AgreementApi();
        return await getAgreements(repo, filters);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchRequiredRegistrationClauses = useCallback(
    async (
      filters?: RegistrationScopeFilters,
    ): Promise<RegistrationAgreementClause[]> => {
      setLoading(true);
      setError(null);
      try {
        const repo = new AgreementApi();
        return await getRequiredRegistrationClauses(repo, filters);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : String(err));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return {
    fetchCountries,
    fetchStates,
    fetchCities,
    fetchDesignations,
    fetchAgreements,
    fetchRequiredRegistrationClauses,
    loading,
    error,
  };
};
